<template>
  <div id="contenedor-listado-desktop">
    <!-- Tarjeta de resumen de avance actual -->
    <v-card class="mb-3" elevation="0" outlined v-if="avanceActual !== null">
      <v-card-text class="py-3">
        <v-row align="center" dense>
          <v-col cols="12" md="4">
            <div class="text-body-2 mb-1 grey--text text--darken-1">
              <v-icon small left color="primary">mdi-chart-timeline-variant</v-icon>
              Avance Físico Real
            </div>
            <v-progress-linear
              :value="avanceActual"
              :color="getColorAvance(avanceActual)"
              height="20"
              rounded
              class="mb-1">
              <strong class="white--text text-body-2">{{ avanceActual }}%</strong>
            </v-progress-linear>
            <div class="text-caption grey--text">
              Último reporte: {{ ultimaFechaReporte }}
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="avanceProgramadoHoy !== null">
            <div class="text-body-2 mb-1 grey--text text--darken-1">
              <v-icon small left color="info">mdi-calendar-clock</v-icon>
              Avance Programado
            </div>
            <v-progress-linear
              :value="avanceProgramadoHoy"
              color="info"
              height="20"
              rounded
              class="mb-1">
              <strong class="white--text text-body-2">{{ avanceProgramadoHoy.toFixed(2) }}%</strong>
            </v-progress-linear>
            <div class="text-caption grey--text">
              Al día: {{ $moment().format('DD/MM/YYYY') }}
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="variacionAvance !== null">
            <div class="text-body-2 mb-1 grey--text text--darken-1">
              <v-icon small left :color="getColorVariacion(variacionAvance)">
                mdi-compare-horizontal</v-icon>
              Variación
            </div>
            <v-chip
              small
              :color="getColorVariacion(variacionAvance)"
              dark
              class="px-4">
              <v-icon small left>{{ getIconoVariacion(variacionAvance) }}</v-icon>
              <span class="text-body-2 font-weight-bold">{{ variacionAvance > 0 ? '+' : '' }}
                {{ variacionAvance.toFixed(2) }}%</span>
            </v-chip>
            <div class="text-caption mt-1" :class="getClassVariacion(variacionAvance)">
              {{ getTextoVariacion(variacionAvance) }}
            </div>
          </v-col>
        </v-row>

        <!-- Botón para expandir análisis de tendencia -->
        <v-divider v-if="false" class="my-2"></v-divider>
        <div v-if="false" class="text-center">
          <v-btn
            text
            x-small
            @click="mostrarAnalisisTendencia = !mostrarAnalisisTendencia"
            :color="mostrarAnalisisTendencia ? 'primary' : 'grey'">
            <v-icon small left>
              {{ mostrarAnalisisTendencia ? 'mdi-chevron-up' : 'mdi-chart-line' }}</v-icon>
            {{ mostrarAnalisisTendencia ? 'Ocultar' : 'Ver' }} Análisis de Tendencia
          </v-btn>
        </div>

        <!-- Sección expandible de análisis -->
        <v-expand-transition>
          <div v-show="mostrarAnalisisTendencia" class="mt-3">
            <v-divider class="mb-3"></v-divider>

            <v-row dense>
              <!-- Velocidad de Avance -->
              <v-col cols="12" md="6">
                <v-card flat class="pa-3" color="grey lighten-5">
                  <div class="text-caption grey--text text--darken-1 mb-2">
                    <v-icon x-small left color="primary">mdi-speedometer</v-icon>
                    VELOCIDAD DE AVANCE
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-h6 font-weight-bold">
                        {{ velocidadAvanceSemanal.toFixed(2) }}%</div>
                      <div class="text-caption">por semana</div>
                    </div>
                    <v-chip
                      small
                      :color="getColorRendimiento(rendimientoContratista)"
                      dark>
                      <v-icon x-small left>
                        {{ getIconoRendimiento(rendimientoContratista) }}</v-icon>
                      {{ rendimientoContratista.toFixed(0) }}% eficiencia
                    </v-chip>
                  </div>
                  <div class="text-caption grey--text mt-2">
                    <v-icon x-small left>mdi-target</v-icon>
                    <template v-if="esPlazoVencido">
                      <span class="error--text font-weight-bold">Plazo vencido:</span>
                      <strong>{{ velocidadRequerida.toFixed(2) }}% semanal</strong>
                      <span class="text-caption">
                        (para terminar en {{ semanasParaRecuperar }} semanas)</span>
                    </template>
                    <template v-else>
                      Requerido: <strong>{{ velocidadRequerida.toFixed(2) }}% semanal</strong>
                    </template>
                  </div>
                </v-card>
              </v-col>

              <!-- Proyección -->
              <v-col cols="12" md="6">
                <v-card flat class="pa-3" color="grey lighten-5">
                  <div class="text-caption grey--text text--darken-1 mb-2">
                    <v-icon x-small left color="info">mdi-calendar-end</v-icon>
                    PROYECCIÓN DE TÉRMINO
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-h6 font-weight-bold">{{ fechaProyectadaTermino }}</div>
                      <div class="text-caption">fecha estimada</div>
                    </div>
                    <v-chip
                      small
                      :color="diasDiferencia > 0 ? 'error' : 'success'"
                      dark>
                      <v-icon x-small left>
                        {{ diasDiferencia > 0 ? 'mdi-alert' : 'mdi-check' }}</v-icon>
                      {{ diasDiferencia > 0 ? '+' : '' }}{{ diasDiferencia }} días
                    </v-chip>
                  </div>
                  <div class="text-caption grey--text mt-2">
                    <v-icon x-small left>mdi-flag-checkered</v-icon>
                    Programado: <strong>{{ fechaProgramadaTermino }}</strong>
                  </div>
                </v-card>
              </v-col>

              <!-- Plan de Recuperación (solo si está atrasado) -->
              <v-col cols="12" v-if="variacionAvance < -2">
                <v-alert
                  dense
                  text
                  :color="planRecuperacion.factible ? 'warning' : 'error'"
                  border="left"
                  class="mb-0">
                  <v-row align="center" dense>
                    <v-col cols="12" md="8">
                      <div class="text-body-2 font-weight-bold mb-1">
                        <v-icon small left>mdi-alert-circle</v-icon>
                        {{ planRecuperacion.titulo }}
                      </div>
                      <div class="text-caption">
                        {{ planRecuperacion.mensaje }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="4" class="text-right">
                      <div class="text-caption grey--text text--darken-1">Velocidad necesaria:</div>
                      <div class="text-h6 font-weight-bold">
                        {{ planRecuperacion.velocidadNecesaria.toFixed(2) }}%
                      </div>
                      <div class="text-caption">por semana</div>
                    </v-col>
                  </v-row>
                </v-alert>
              </v-col>

              <!-- Tendencia últimas semanas -->
              <v-col cols="12">
                <v-card flat class="pa-3" color="grey lighten-5">
                  <div class="text-caption grey--text text--darken-1 mb-2">
                    <v-icon x-small left color="purple">mdi-chart-timeline-variant</v-icon>
                    TENDENCIA ÚLTIMAS 4 SEMANAS
                  </div>
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="getColorTendencia(tendenciaReciente)"
                      dark
                      class="mr-2">
                      <v-icon small left>{{ getIconoTendencia(tendenciaReciente) }}</v-icon>
                      {{ getTextoTendencia(tendenciaReciente) }}
                    </v-chip>
                    <div class="text-caption grey--text">
                      {{ mensajeTendencia }}
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <!-- Filtros rápidos -->
    <v-row class="mb-3">
      <v-col cols="12">
        <v-chip-group
          v-model="filtroSeleccionado"
          active-class="primary--text"
          mandatory>
          <v-chip filter outlined>
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            Todos ({{ getReportesFisicos.length }})
          </v-chip>
          <v-chip filter outlined>
            <v-icon left small color="orange">mdi-alert</v-icon>
            Con Problemas ({{ reportesConProblemas }})
          </v-chip>
          <v-chip filter outlined>
            <v-icon left small color="success">mdi-check-circle</v-icon>
            Revisados ({{ reportesRevisados }})
          </v-chip>
          <v-chip filter outlined>
            <v-icon left small color="grey">mdi-clock-outline</v-icon>
            Pendientes ({{ reportesPendientes }})
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="reportesFiltrados"
      :items-per-page="20"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, -1],
        itemsPerPageText: 'Reportes por página:',
        showFirstLastPage: true,
      }"
      :loading="loading"
      loading-text="Cargando reportes físicos..."
      class="elevation-1">
      <template v-slot:[`item.numero`]="{ item }">
        <v-chip
          small
          :color="item.numero === getReportesFisicos.length ? 'primary' : 'grey lighten-2'"
          :dark="item.numero === getReportesFisicos.length">
          <strong># {{ item.numero }}</strong>
        </v-chip>
      </template>
      <template v-slot:[`item.fecha`]="{ item }">
        {{ $moment(item.fecha).format('DD/MM/YYYY') }}
      </template>
      <template v-slot:[`item.existeProblema`]="{ item }">
        <v-tooltip top v-if="item.existeProblema">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              color="orange">
              mdi-alert
            </v-icon>
          </template>
          <span>Reporta problema</span>
        </v-tooltip>
        <v-icon v-else color="grey lighten-1" small>
          mdi-check
        </v-icon>
      </template>
      <template v-slot:[`item.avanceFisico`]="{ item }">
        <div class="text-center">
          <div class="text-subtitle-1 font-weight-bold">
            {{ limitarAvance(item.avanceFisico) }}%
          </div>
          <div class="text-caption grey--text">acumulado</div>
        </div>
      </template>
      <template v-slot:[`item.avancePeriodo`]="{ item }">
        <v-chip
          small
          :color="getColorAvancePeriodo(getAvancePeriodo(item))"
          dark>
          <v-icon left x-small>mdi-trending-up</v-icon>
          <strong>+{{ getAvancePeriodo(item).toFixed(2) }}%</strong>
        </v-chip>
      </template>
      <template v-slot:[`item.calidad`]="{ item }">
        <v-chip
          small
          :color="getColorCalidad(item.calidad)"
          dark>
          {{ getTextoCalidad(item.calidad) }}
        </v-chip>
      </template>
      <template v-slot:[`item.partidas`]="{ item }">
        <v-chip small color="indigo lighten-4">
          <b>{{ item.partidas.length }}</b>
        </v-chip>
      </template>
      <template v-slot:[`item.comentarios`]="{ item }">
        <small>
          <cortar-texto
          :texto="item.comentarios">
          </cortar-texto>
        </small>
      </template>
      <template v-slot:[`item.descripcionProblema`]="{ item }">
        <small>
          <cortar-texto
          :texto="item.descripcionProblema ? item.descripcionProblema : ''">
          </cortar-texto>
        </small>
      </template>
      <template v-slot:[`item.fechaHoraRevisado`]="{ item }">
        <SwitchRevisado v-if="!setEsContratista" :reporteFisico="item" />
        <template v-else-if="item.fechaHoraRevisado">
          <v-row justify="center">
            <v-icon  color="success">
              mdi-check-circle-outline
            </v-icon>
          </v-row>
        </template>
      </template>
      <template v-slot:[`item.acciones`]="{ item }">
        <div class="d-flex justify-end">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="verResporte(item)">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Ver Reporte</span>
          </v-tooltip>

          <v-tooltip
            v-if="item.numero == getReportesFisicos.length
              && mixValidaScopesActions(2)
              && revisadoAndContratista(item)"
            top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                color="info"
                v-on="on"
                @click="editarReporteFisico(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>

          <v-tooltip
            top
            v-if="item.numero == getReportesFisicos.length && mixValidaScopesActions(3)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                v-bind="attrs"
                v-on="on"
                @click="eliminarReporteFisico(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Eliminar</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <v-dialog
      v-model="mostrarVentanaReporteFisico"
      fullscreen
      transition="dialog-bottom-transition">
      <detalles-reporte
        :reporteFisico="reporteSeleccionado"
        v-on:cerrar="mostrarVentanaReporteFisico = false">
      </detalles-reporte>
    </v-dialog>
    <!-- <v-dialog
      v-model="mostrarVentanaEditarReporte"
      fullscreen>
      <editar-reporte
        v-if="mostrarVentanaEditarReporte"
        :ultimoReporteFisico="ultimoReporteFisico"
        v-on:cerrar="mostrarVentanaEditarReporte = false">
      </editar-reporte>
    </v-dialog> -->
  </div>
</template>

<script>
import eventBus from '@/helpers/eventBus';
import { limitarAvanceFisico } from '@/components/administracion/ejecucion/reportes/avanceFisico';
import DetallesReporte from '@/components/administracion/ejecucion/reportes/DetallesReporte.vue';
import CortarTexto from '@/components/CortarTextos.vue';
import SwitchRevisado from '@/components/administracion/ejecucion/reportes/FechaHoraRevisadoCheck.vue';
// import EditarReporte from '@/components/administracion/ejecucion/reportes/editar/Editar.vue';
// import Opciones from './Opciones.vue';

export default {
  name: 'ListadoDesktop',
  components: {
    // Opciones,
    DetallesReporte,
    CortarTexto,
    SwitchRevisado,
    // EditarReporte,
  },
  props: {
    reportesFisicos: Array,
    programaContrato: {
      type: [Object, Array],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      filtroSeleccionado: 0,
      mostrarVentanaEditarReporte: false,
      mostrarAnalisisTendencia: false,
      ultimoReporteFisico: {},
      headers: [
        {
          text: 'Número',
          align: 'center',
          sortable: true,
          value: 'numero',
          width: '100px',
        },
        {
          text: 'Fecha',
          align: 'center',
          sortable: true,
          value: 'fecha',
          width: '120px',
        },
        {
          text: 'Problema',
          sortable: true,
          value: 'existeProblema',
          align: 'center',
          width: '90px',
        },
        {
          text: 'Avance Acumulado',
          sortable: true,
          value: 'avanceFisico',
          align: 'center',
          width: '140px',
        },
        {
          text: 'Avance del Período',
          sortable: true,
          value: 'avancePeriodo',
          align: 'center',
          width: '160px',
        },
        {
          text: 'Calidad',
          align: 'center',
          sortable: true,
          value: 'calidad',
          width: '110px',
        },
        {
          text: 'Partidas',
          sortable: false,
          align: 'center',
          value: 'partidas',
          width: '1%',
        },
        {
          text: 'Comentarios',
          sortable: false,
          value: 'comentarios',
        },
        {
          text: 'Problema',
          sortable: false,
          value: 'descripcionProblema',
        },
        // { text: 'F. Termino', value: 'fechaTermino' },
        {
          text: 'Revisado',
          sortable: false,
          width: '1%',
          value: 'fechaHoraRevisado',
        },
        {
          text: '',
          value: 'acciones',
          sortable: false,
          align: 'end',
          width: '150',
        },
      ],
      mostrarVentanaReporteFisico: false,
      reporteSeleccionado: {},
    };
  },
  methods: {
    limitarAvance: limitarAvanceFisico,
    revisadoAndContratista(reporte) {
      if (this.setEsContratista && reporte.fechaHoraRevisado) {
        return false;
      }
      return true;
    },
    verResporte(item) {
      // this.reporteSeleccionado = item;
      eventBus.$emit('ver-reporte-fisico', item);
      // this.mostrarVentanaReporteFisico = true;
    },
    // editarEsteReporte(item) {
    //   this.ultimoReporteFisico = item;
    //   this.mostrarVentanaEditarReporte = true;
    // },
    editarReporteFisico(reporte) {
      eventBus.$emit('editar-reporte-fisico', reporte);
    },
    eliminarReporteFisico(reporte) {
      eventBus.$emit('eliminar-reporte-fisico', reporte);
    },
    getColorCalidad(calidad) {
      switch (calidad) {
        case 1:
          return 'green';
        case 2:
          return 'amber';
        default:
          return 'red';
      }
    },
    getTextoCalidad(calidad) {
      switch (calidad) {
        case 1:
          return 'Excelente';
        case 2:
          return 'Regular';
        default:
          return 'Deficiente';
      }
    },
    getColorAvance(avance) {
      if (avance >= 75) return 'success';
      if (avance >= 50) return 'info';
      if (avance >= 25) return 'warning';
      return 'error';
    },
    getAvancePeriodo(item) {
      const reportes = this.getReportesFisicos;
      const index = reportes.findIndex((r) => r.numero === item.numero);

      if (index === reportes.length - 1) {
        // Es el primer reporte (último en el array ordenado)
        return limitarAvanceFisico(item.avanceFisico);
      }

      const reporteAnterior = reportes[index + 1];
      return limitarAvanceFisico(item.avanceFisico)
        - limitarAvanceFisico(reporteAnterior?.avanceFisico || 0);
    },
    getColorAvancePeriodo(avance) {
      if (avance >= 10) return 'success';
      if (avance >= 5) return 'info';
      if (avance >= 2) return 'orange';
      if (avance > 0) return 'warning';
      return 'grey';
    },
    calcularAvanceProgramado(fecha) {
      const programa = this.getProgramaContrato;
      if (!programa) return null;

      const { fechaInicio, fechaTermino } = programa;
      if (!fechaInicio || !fechaTermino) return null;

      const inicio = this.$moment(fechaInicio);
      const termino = this.$moment(fechaTermino);
      const fechaCalculo = this.$moment(fecha);

      // Si la fecha es antes del inicio, el avance es 0
      if (fechaCalculo.isBefore(inicio)) return 0;

      // Si la fecha es después del término, el avance es 100
      if (fechaCalculo.isAfter(termino)) return 100;

      // Calcular días transcurridos y días totales
      const diasTranscurridos = fechaCalculo.diff(inicio, 'days');
      const diasTotales = termino.diff(inicio, 'days');

      // Calcular porcentaje
      const avance = (diasTranscurridos / diasTotales) * 100;
      return avance;
    },
    getColorVariacion(variacion) {
      if (variacion > 5) return 'success';
      if (variacion >= -5) return 'warning';
      return 'error';
    },
    getIconoVariacion(variacion) {
      if (variacion > 0) return 'mdi-trending-up';
      if (variacion < 0) return 'mdi-trending-down';
      return 'mdi-trending-neutral';
    },
    getClassVariacion(variacion) {
      if (variacion > 0) return 'success--text font-weight-bold';
      if (variacion < 0) return 'error--text font-weight-bold';
      return 'grey--text';
    },
    getTextoVariacion(variacion) {
      if (variacion > 5) return 'Adelantado - Excelente ritmo';
      if (variacion > 0) return 'Adelantado';
      if (variacion >= -5) return 'En tiempo - Dentro del margen';
      if (variacion >= -10) return 'Atrasado - Requiere atención';
      return 'Muy atrasado - Acción inmediata';
    },
    calcularReportesPorSemana() {
      if (!this.getReportesFisicos.length) return [];

      const reportes = [...this.getReportesFisicos].reverse(); // Orden cronológico
      const hoy = this.$moment();
      const hace4Semanas = hoy.clone().subtract(4, 'weeks');

      // Intentar obtener reportes de las últimas 4 semanas
      const reportesRecientes = reportes.filter((r) => {
        const fechaReporte = this.$moment(r.fecha);
        return fechaReporte.isAfter(hace4Semanas);
      });

      // Si hay al menos 2 reportes recientes, usarlos
      if (reportesRecientes.length >= 2) {
        return reportesRecientes;
      }

      // Si no hay suficientes reportes recientes, usar todos los reportes disponibles
      // para poder calcular la tendencia histórica
      return reportes;
    },
    getColorRendimiento(rendimiento) {
      if (rendimiento >= 100) return 'success';
      if (rendimiento >= 80) return 'info';
      if (rendimiento >= 60) return 'warning';
      return 'error';
    },
    getIconoRendimiento(rendimiento) {
      if (rendimiento >= 100) return 'mdi-check-circle';
      if (rendimiento >= 80) return 'mdi-check';
      if (rendimiento >= 60) return 'mdi-alert';
      return 'mdi-alert-circle';
    },
    getColorTendencia(tendencia) {
      if (tendencia === 'mejorando') return 'success';
      if (tendencia === 'estable') return 'info';
      return 'error';
    },
    getIconoTendencia(tendencia) {
      if (tendencia === 'mejorando') return 'mdi-trending-up';
      if (tendencia === 'estable') return 'mdi-trending-neutral';
      return 'mdi-trending-down';
    },
    getTextoTendencia(tendencia) {
      if (tendencia === 'mejorando') return 'Mejorando';
      if (tendencia === 'estable') return 'Estable';
      return 'Empeorando';
    },
  },
  computed: {
    getReportesFisicos() {
      if (!this.reportesFisicos) return [];
      return this.reportesFisicos;
    },
    reportesFiltrados() {
      const reportes = this.getReportesFisicos;

      switch (this.filtroSeleccionado) {
        case 1: // Con Problemas
          return reportes.filter((r) => r.existeProblema);
        case 2: // Revisados
          return reportes.filter((r) => r.fechaHoraRevisado);
        case 3: // Pendientes
          return reportes.filter((r) => !r.fechaHoraRevisado);
        default: // Todos
          return reportes;
      }
    },
    reportesConProblemas() {
      return this.getReportesFisicos.filter((r) => r.existeProblema).length;
    },
    reportesRevisados() {
      return this.getReportesFisicos.filter((r) => r.fechaHoraRevisado).length;
    },
    velocidadAvanceSemanal() {
      const reportesRecientes = this.calcularReportesPorSemana();
      if (reportesRecientes.length < 2) return 0;

      const primerReporte = reportesRecientes[0];
      const ultimoReporte = reportesRecientes[reportesRecientes.length - 1];

      const avanceTotal = ultimoReporte.avanceFisico - primerReporte.avanceFisico;
      const fechaInicio = this.$moment(primerReporte.fecha);
      const fechaFin = this.$moment(ultimoReporte.fecha);
      const semanas = fechaFin.diff(fechaInicio, 'weeks', true);

      // Evitar división por cero y asegurar al menos 1 semana
      if (semanas <= 0) {
        // Si los reportes están en días muy cercanos, calcular por días
        const dias = fechaFin.diff(fechaInicio, 'days');
        if (dias > 0) {
          return (avanceTotal / dias) * 7; // Convertir a semanal
        }
        return 0;
      }

      return avanceTotal / semanas;
    },
    velocidadRequerida() {
      const programa = this.getProgramaContrato;
      if (!programa) return 0;

      const hoy = this.$moment();
      const termino = this.$moment(programa.fechaTermino);
      const semanasRestantes = termino.diff(hoy, 'weeks', true);
      const avanceFaltante = 100 - this.avanceActual;

      // Si el avance ya está completo
      if (avanceFaltante <= 0) return 0;

      // Si aún hay tiempo en el plazo original
      if (semanasRestantes > 0) {
        return avanceFaltante / semanasRestantes;
      }

      // Si el plazo ya venció, calcular velocidad necesaria para terminar
      // en un tiempo realista (mínimo 2 semanas para no ser imposible)
      // Usamos el mayor entre: 2 semanas o el tiempo estimado al ritmo actual
      if (this.velocidadAvanceSemanal > 0) {
        const semanasNecesariasAlRitmoActual = avanceFaltante / this.velocidadAvanceSemanal;
        const semanasMinimas = Math.max(2, Math.ceil(semanasNecesariasAlRitmoActual * 0.5));
        return avanceFaltante / semanasMinimas;
      }

      // Si no hay velocidad actual, asumir un mínimo de 4 semanas para completar
      return avanceFaltante / 4;
    },
    rendimientoContratista() {
      if (this.velocidadRequerida === 0) return 100;
      return (this.velocidadAvanceSemanal / this.velocidadRequerida) * 100;
    },
    fechaProyectadaTermino() {
      if (this.velocidadAvanceSemanal <= 0) return 'No estimable';

      const avanceFaltante = 100 - this.avanceActual;
      const semanasNecesarias = avanceFaltante / this.velocidadAvanceSemanal;
      const fechaProyectada = this.$moment().add(semanasNecesarias, 'weeks');

      return fechaProyectada.format('DD/MM/YYYY');
    },
    fechaProgramadaTermino() {
      const programa = this.getProgramaContrato;
      if (!programa) return '';
      return this.$moment(programa.fechaTermino).format('DD/MM/YYYY');
    },
    diasDiferencia() {
      if (this.velocidadAvanceSemanal <= 0) return 0;

      const programa = this.getProgramaContrato;
      if (!programa) return 0;

      const avanceFaltante = 100 - this.avanceActual;
      const semanasNecesarias = avanceFaltante / this.velocidadAvanceSemanal;
      const fechaProyectada = this.$moment().add(semanasNecesarias, 'weeks');
      const fechaProgramada = this.$moment(programa.fechaTermino);

      return fechaProyectada.diff(fechaProgramada, 'days');
    },
    planRecuperacion() {
      const programa = this.getProgramaContrato;
      if (!programa) {
        return {
          titulo: 'Sin programa',
          mensaje: 'No se puede calcular',
          velocidadNecesaria: 0,
          factible: false,
        };
      }

      const hoy = this.$moment();
      const termino = this.$moment(programa.fechaTermino);
      const semanasRestantes = termino.diff(hoy, 'weeks', true);
      const avanceFaltante = 100 - this.avanceActual;

      // Caso: Plazo ya vencido
      if (semanasRestantes <= 0) {
        const velocidadNecesaria = this.velocidadRequerida;
        const incrementoRequerido = this.velocidadAvanceSemanal > 0
          ? ((velocidadNecesaria / this.velocidadAvanceSemanal) - 1) * 100
          : 200; // Si no hay velocidad, necesita 200% más

        const diasVencido = Math.abs(Math.floor(semanasRestantes * 7));

        return {
          titulo: `Fuera de plazo (${diasVencido} días de retraso)`,
          mensaje: `El plazo venció el ${this.$moment(programa.fechaTermino).format('DD/MM/YYYY')}. Se requiere aumentar la velocidad en ${incrementoRequerido.toFixed(0)}% para minimizar el retraso adicional.`,
          velocidadNecesaria,
          factible: incrementoRequerido <= 100,
        };
      }

      // Caso: Aún dentro del plazo
      const velocidadNecesaria = avanceFaltante / semanasRestantes;
      const incrementoRequerido = this.velocidadAvanceSemanal > 0
        ? ((velocidadNecesaria / this.velocidadAvanceSemanal) - 1) * 100
        : 100;

      let factible = false;
      let titulo = '';
      let mensaje = '';

      if (incrementoRequerido <= 20) {
        factible = true;
        titulo = 'Recuperación Factible';
        mensaje = `Se requiere aumentar la velocidad en ${incrementoRequerido.toFixed(0)}%. Es alcanzable con esfuerzo adicional.`;
      } else if (incrementoRequerido <= 50) {
        factible = true;
        titulo = 'Recuperación Difícil';
        mensaje = `Se requiere aumentar la velocidad en ${incrementoRequerido.toFixed(0)}%. Requiere recursos adicionales.`;
      } else {
        factible = false;
        titulo = 'Recuperación Crítica';
        mensaje = `Se requiere aumentar la velocidad en ${incrementoRequerido.toFixed(0)}%. Poco factible sin ampliación de plazo.`;
      }

      return {
        titulo,
        mensaje,
        velocidadNecesaria,
        factible,
      };
    },
    tendenciaReciente() {
      const reportesRecientes = this.calcularReportesPorSemana();
      if (reportesRecientes.length < 3) return 'estable';

      // Calcular avances de cada semana
      const avances = [];
      for (let i = 1; i < reportesRecientes.length; i += 1) {
        avances.push(reportesRecientes[i].avanceFisico - reportesRecientes[i - 1].avanceFisico);
      }

      // Comparar primera mitad vs segunda mitad
      const mitad = Math.floor(avances.length / 2);
      const promedioInicio = avances.slice(0, mitad).reduce((a, b) => a + b, 0) / mitad;
      const promedioFinal = avances.slice(mitad)
        .reduce((a, b) => a + b, 0) / (avances.length - mitad);

      if (promedioFinal > promedioInicio * 1.1) return 'mejorando';
      if (promedioFinal < promedioInicio * 0.9) return 'empeorando';
      return 'estable';
    },
    mensajeTendencia() {
      const tendencia = this.tendenciaReciente;
      if (tendencia === 'mejorando') {
        return 'El ritmo de trabajo está aumentando';
      }
      if (tendencia === 'empeorando') {
        return 'El ritmo de trabajo está disminuyendo';
      }
      return 'El ritmo de trabajo se mantiene constante';
    },
    reportesPendientes() {
      return this.getReportesFisicos.filter((r) => !r.fechaHoraRevisado).length;
    },
    setEsContratista() {
      const { roles } = this.$auth.user;
      return roles.includes('CONTRATISTA:SUPERVISION');
    },
    getProgramaContrato() {
      // Validar que programaContrato exista y tenga las fechas necesarias
      if (!this.programaContrato) {
        console.warn('No hay programa del contrato disponible');
        return null;
      }

      // Si programaContrato es un objeto con fechas, usarlo directamente
      if (this.programaContrato.fechaInicio && this.programaContrato.fechaTermino) {
        console.log('Programa encontrado:', this.programaContrato);
        return this.programaContrato;
      }

      console.warn('Programa sin fechas válidas:', this.programaContrato);
      return null;
    },
    avanceActual() {
      if (!this.getReportesFisicos.length) return null;
      return limitarAvanceFisico(this.getReportesFisicos[0]?.avanceFisico || 0);
    },
    ultimaFechaReporte() {
      if (!this.getReportesFisicos.length) return '';
      const fecha = this.getReportesFisicos[0]?.fecha;
      return this.$moment(fecha).format('DD/MM/YYYY');
    },
    avanceProgramadoHoy() {
      return this.calcularAvanceProgramado(new Date());
    },
    variacionAvance() {
      if (this.avanceActual === null || this.avanceProgramadoHoy === null) return null;
      return this.avanceActual - this.avanceProgramadoHoy;
    },
    esPlazoVencido() {
      const programa = this.getProgramaContrato;
      if (!programa) return false;

      const hoy = this.$moment();
      const termino = this.$moment(programa.fechaTermino);
      return hoy.isAfter(termino);
    },
    semanasParaRecuperar() {
      const avanceFaltante = 100 - this.avanceActual;
      if (this.velocidadRequerida <= 0 || avanceFaltante <= 0) return 0;

      return Math.ceil(avanceFaltante / this.velocidadRequerida);
    },
  },
};
</script>

<style lang="scss">

</style>
